import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ListChecks, Camera, Clock, Stethoscope, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { fetchPortalTrack, type PortalTrackRow } from '@/lib/queries';
import StepLine from '@/components/portal/StepLine';
import { cn } from '@/lib/cn';

const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' }) : '';
const chip = (on: boolean, tone: 'accent' | 'red' | 'amber' = 'accent') => cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition',
  on ? (tone === 'red' ? 'border-red-400 bg-red-500/15 text-red-300' : tone === 'amber' ? 'border-amber-400 bg-amber-500/15 text-amber-300' : 'border-accent-400 bg-accent-600/20 text-accent-400')
     : (tone === 'red' ? 'border-red-500/40 text-red-300' : tone === 'amber' ? 'border-amber-500/40 text-amber-300' : 'border-slate-800 text-muted hover:text-muted-strong'));

/** Every piece the login can see, where it is, who holds it, since when. Computed
 *  from what actually happened, the same engine as KAF's own tracker. */
export default function PortalTrackPage() {
  const [doc, setDoc] = useState('');
  const [next, setNext] = useState('');
  const [showDone, setShowDone] = useState(false);
  const q = useQuery({ queryKey: ['portal-track', showDone], queryFn: () => fetchPortalTrack(showDone), refetchInterval: 30000 });
  const d = q.data;

  const rows = useMemo(() => (d?.rows ?? []).filter((r) => (!doc || r.talent_id === doc)
    && (!next || (next === 'you' ? r.waiting_on_you : next === 'doctor' ? r.waiting_on_doctor : next === 'overdue' ? r.overdue : r.next_key === next))), [d, doc, next]);
  const groups = useMemo(() => {
    const m = new Map<string, PortalTrackRow[]>();
    for (const r of rows) {
      const k = r.current_stage === 'posted' || r.next_key === 'done' ? 'Posted' : r.next_label;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    for (const list of m.values()) list.sort((a, b) => (b.days ?? 0) - (a.days ?? 0));
    return Array.from(m.entries()).sort((a, b) => (a[0] === 'Posted' ? 1 : b[0] === 'Posted' ? -1 : b[1].length - a[1].length));
  }, [rows]);
  const s = d?.summary;
  const byNext = Object.entries(s?.by_next ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-muted-strong inline-flex items-center gap-2"><ListChecks size={18} /> Tracker</h1>
        <p className="text-xs text-muted mt-1">Every piece, where it is right now, who holds it and since when. Updated from what actually happened.</p>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Tile label="Open" value={s.open} icon={<ListChecks size={12} />} />
          <Tile label="Waiting on you" value={s.waiting_on_you} tone={s.waiting_on_you ? 'accent' : undefined} icon={<Clock size={12} />} />
          <Tile label="With your doctors" value={s.waiting_on_doctor} tone={s.waiting_on_doctor ? 'amber' : undefined} icon={<Stethoscope size={12} />} />
          <Tile label="Past the expected date" value={s.overdue} tone={s.overdue ? 'red' : undefined} icon={<AlertTriangle size={12} />} />
          <Tile label="Shoots next 7 days" value={s.shoots_next_7} icon={<Camera size={12} />} />
          <Tile label="Posted" value={s.posted} icon={<CheckCircle2 size={12} />} />
        </div>
      )}

      {d && d.doctors.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="track-doctors">
          <button onClick={() => setDoc('')} className={chip(!doc)}>All doctors</button>
          {d.doctors.map((t) => (
            <button key={t.id} onClick={() => setDoc(t.id)} className={chip(doc === t.id)} data-testid="track-doctor-chip">{t.me ? `You (${t.name})` : t.name} <span className="font-mono opacity-70">{t.open}</span></button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid="track-next">
        <button onClick={() => setNext('')} className={chip(!next)}>All</button>
        {(s?.waiting_on_you ?? 0) > 0 && <button onClick={() => setNext('you')} className={chip(next === 'you')}>On you <span className="font-mono opacity-70">{s?.waiting_on_you}</span></button>}
        {(s?.waiting_on_doctor ?? 0) > 0 && <button onClick={() => setNext('doctor')} className={chip(next === 'doctor', 'amber')}>With the doctor <span className="font-mono opacity-70">{s?.waiting_on_doctor}</span></button>}
        {(s?.overdue ?? 0) > 0 && <button onClick={() => setNext('overdue')} className={chip(next === 'overdue', 'red')}>Late <span className="font-mono opacity-70">{s?.overdue}</span></button>}
        {byNext.filter(([k]) => !['client', 'doctor', 'doctor_link'].includes(k)).map(([k, n]) => (
          <button key={k} onClick={() => setNext(k)} className={chip(next === k)}>{s?.labels[k] ?? k} <span className="font-mono opacity-70">{n}</span></button>
        ))}
        <label className="ml-auto inline-flex items-center gap-2 text-xs text-muted min-h-[36px]"><input type="checkbox" className="accent-accent-400" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Include posted</label>
      </div>

      {q.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {!q.isLoading && groups.length === 0 && <div className="rounded-xl border border-dashed border-slate-800 py-10 text-center text-sm text-muted">Nothing open.</div>}
      {groups.map(([k, list]) => (
        <section key={k} className="space-y-2">
          <div className="flex items-baseline gap-2">
            <h2 className="text-xs uppercase tracking-wide text-muted">{k}</h2>
            <span className="text-[11px] text-muted/70">{list.length}</span>
          </div>
          {list.map((r) => (
            <div key={r.creative_id} className={cn('rounded-xl border bg-bg-card px-4 py-3', r.waiting_on_you ? 'border-accent-400/40' : r.overdue ? 'border-red-500/40' : 'border-slate-800')} data-testid="track-row">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="font-mono text-[10px] text-muted">{r.code}</span>
                <span className="text-muted-strong font-medium truncate">{r.title}</span>
                <span className="text-[10px] uppercase text-muted">{r.creative_type}</span>
                {r.talent_name && <span className="text-[10px] text-muted">{r.talent_name}</span>}
                <span className={cn('ml-auto text-xs text-right', r.waiting_on_you ? 'text-accent-300 font-medium' : r.overdue ? 'text-red-300' : 'text-muted')}>
                  {r.current_stage === 'posted' ? `Posted${r.posting_date ? ` · ${r.posting_date}` : ''}` : r.next_label}
                  {r.current_stage !== 'posted' && r.owner && r.owner !== 'KAF' && <span className="text-muted"> · with {r.owner}</span>}
                  {r.current_stage !== 'posted' && r.days > 0 && <span className="text-muted"> · {r.days}d</span>}
                </span>
              </div>
              <StepLine steps={r.steps} dates />
              <div className="flex items-center gap-3 mt-1 text-[11px] text-muted flex-wrap">
                {r.shoot_at && <span className="inline-flex items-center gap-1"><Camera size={10} /> shoot {fmt(r.shoot_at)}</span>}
                {r.expected_by && r.current_stage !== 'posted' && <span className={cn('inline-flex items-center gap-1', r.overdue && 'text-red-300')}><Clock size={10} /> expected by {fmt(r.expected_by)}</span>}
                {r.client_visible && <Link to={`/portal/creatives/${r.creative_id}`} className="text-accent-400 hover:underline ml-auto">Open</Link>}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function Tile({ label, value, tone, icon }: { label: string; value: number; tone?: 'accent' | 'red' | 'amber'; icon?: React.ReactNode }) {
  return (
    <div className={cn('rounded-xl border px-4 py-3', tone === 'accent' ? 'border-accent-400/40 bg-accent-600/10' : 'border-slate-800 bg-bg-card')}>
      <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1">{icon}{label}</div>
      <div className={cn('text-2xl font-semibold tabular-nums', tone === 'accent' ? 'text-accent-300' : tone === 'red' ? 'text-red-300' : tone === 'amber' ? 'text-amber-300' : 'text-muted-strong')}>{value ?? 0}</div>
    </div>
  );
}
